import React, { useCallback } from "react";
import { View, StyleSheet, Pressable, Text, ActivityIndicator, Linking } from "react-native";
import PropTypes from "prop-types";
import { platform, logError } from "../platform";
import { COLORS } from "../theme/colors";

/**
 * Chat bot service URL
 * @constant
 */
const CHAT_URL = process.env.REACT_APP_AI_CHATBOT_URL;

/**
 * Mobile breakpoint for responsive layout
 * @constant
 */
const MOBILE_BREAKPOINT = 768;

const chatBotIsNative = platform === "native" ? true : false;

/**
 * Platform-specific WebView import.
 * Only loaded on native platforms (iOS/Android), null on web.
 */
const WebView = platform === "web" ? null : require("react-native-webview").WebView;

/**
 * ChatBot component renders a chat support interface.
 * - Uses WebView for native platforms.
 * - Uses iframe for web platforms (mobile and desktop).
 * - Handles loading and error states.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} [props.navigation] - Navigation object for closing the chat
 * @param {boolean} [props.isModal] - Whether the component is rendered in a modal (web only)
 * @returns {JSX.Element} ChatBot UI
 */
const ChatBot = ({ navigation, isModal = false }) => {
  /**
   * State: loading indicator for chat interface
   * @type {[boolean, Function]}
   */
  const [isLoading, setIsLoading] = React.useState(true);
  /**
   * State: error indicator for chat interface
   * @type {[boolean, Function]}
   */
  const [hasError, setHasError] = React.useState(false);
  /**
   * State: tracks if viewport is mobile size (web only)
   * @type {[boolean, Function]}
   */
  const [isMobile, setIsMobile] = React.useState(
    platform === "web" && typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  /**
   * Cleanup flag to prevent state updates on unmounted component
   */
  const mountedRef = React.useRef(true);

  /**
   * Ref for WebView instance to allow explicit cleanup on unmount
   */
  const webViewRef = React.useRef(null);

  /**
   * Handle window resize for responsive layout (web only)
   */
  React.useEffect(() => {
    if (platform !== "web" || typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * Cleanup effect to prevent state updates after unmount and break WebView retain cycles
   */
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      webViewRef.current = null;
    };
  }, []);

  /**
   * Handles closing the chat interface.
   * Navigates back if navigation prop is provided.
   */
  const handleClose = React.useCallback(() => {
    if (navigation) {
      navigation.goBack();
    }
  }, [navigation]);

  /**
   * Callback for when chat iframe/WebView finishes loading.
   */
  const handleLoadEnd = React.useCallback(() => {
    if (mountedRef.current) {
      setIsLoading(false);
    }
  }, []);

  /**
   * Callback for when chat iframe/WebView encounters an error.
   */
  const handleError = React.useCallback(() => {
    if (mountedRef.current) {
      setIsLoading(false);
      setHasError(true);
    }
  }, []);

  /**
   * IFrame element for web platforms.
   * Memoized to avoid recreation on every render.
   */
  const ChatIFrame = React.useMemo(
    () => (
      <iframe
        src={`${CHAT_URL}?isNative=${chatBotIsNative}`}
        title="Chat Bot Service"
        allow="clipboard-read; clipboard-write"
        style={styles.iframe}
        onLoad={handleLoadEnd}
        onError={handleError}
      />
    ),
    [handleLoadEnd, handleError]
  );

  /**
   * Renders error UI with message and close button.
   * @returns {JSX.Element} Error container
   */
  const renderError = () => (
    <View style={styles.errorContainer}>
      <Text style={styles.errorText} accessible={true} accessibilityRole="text">
        Unable to load chat. Please try again later.
      </Text>
      <Pressable
        onPress={handleClose}
        style={styles.errorButton}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel="Close chat"
      >
        <Text style={styles.errorButtonText}>Close</Text>
      </Pressable>
    </View>
  );

  /**
   * Renders loading UI with spinner and text.
   * @returns {JSX.Element} Loading container
   */
  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={COLORS.primary} />
      <Text style={styles.loadingText}>Loading chat...</Text>
    </View>
  );

  /**
   * Determines whether the WebView should load a requested URL.
   * Intercepts external links and opens them in the system browser instead of within the WebView.
   * Only allows URLs that start with the chat service URL to load within the WebView.
   *
   * @param {Object} request - The navigation request object
   * @param {string} request.url - The URL being requested
   * @returns {boolean} true to allow WebView to load the URL, false to prevent loading
   */
  const shouldStartLoadWithRequest = useCallback((request) => {
    if (!request.url.startsWith(CHAT_URL)) {
      try {
        Linking.openURL(request.url);
      } catch (error) {
        logError("chatbot_link_failed", {
          message: error.message,
          stack: error.stack,
          url: request.url,
        });
      }
      return false;
    }
    return true;
  }, []);

  /**
   * Renders WebView for native platforms.
   * Extracted to avoid duplication.
   * @returns {JSX.Element} WebView component
   */
  const renderWebView = () => (
    <WebView
      ref={webViewRef}
      source={{ uri: `${CHAT_URL}?isNative=${chatBotIsNative}` }}
      style={styles.webview}
      javaScriptEnabled={true}
      domStorageEnabled={true}
      startInLoadingState={true}
      onLoadEnd={handleLoadEnd}
      onError={handleError}
      renderLoading={renderLoading}
      onShouldStartLoadWithRequest={shouldStartLoadWithRequest}
    />
  );

  // Native always uses full-screen (ignores isModal)
  if (platform === "native") {
    return <View style={styles.container}>{hasError ? renderError() : renderWebView()}</View>;
  }

  // Web modal/popover
  if (isModal) {
    return (
      <View style={styles.popoverContainer}>
        {hasError ? renderError() : ChatIFrame}
        {isLoading && !hasError && renderLoading()}
      </View>
    );
  }

  // For web, show full screen on mobile, centered on desktop
  if (platform === "web") {
    return isMobile ? (
      <View style={styles.mobileContainer}>
        <View style={styles.mobileContent}>
          {hasError ? renderError() : ChatIFrame}
          {isLoading && !hasError && renderLoading()}
        </View>
      </View>
    ) : (
      <View style={styles.webDesktopContainer}>
        <View style={styles.webDesktopContent}>
          {hasError ? renderError() : ChatIFrame}
          {isLoading && !hasError && renderLoading()}
        </View>
      </View>
    );
  }

  return null;
};

/**
 * PropTypes for ChatBot component
 */
ChatBot.propTypes = {
  /** Navigation object for closing the chat */
  navigation: PropTypes.object,
  /** Whether the component is rendered in a modal */
  isModal: PropTypes.bool,
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.background,
    height: "95%",
  },
  webview: {
    flex: 1,
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
    margin: 0,
    padding: 0,
    flex: 1,
  },
  popoverContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    backgroundColor: "transparent",
  },
  // Mobile full-screen styles
  mobileContainer: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  mobileContent: {
    flex: 1,
    position: "relative",
  },
  // Web desktop centered view
  webDesktopContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  webDesktopContent: {
    width: "100%",
    maxWidth: 600,
    height: "90%",
    maxHeight: 602,
    backgroundColor: COLORS.textLight,
    borderRadius: 12,
    overflow: "hidden",
    ...(platform === "web"
      ? { boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)" }
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
        }),
    position: "relative",
  },
  // Loading styles
  loadingContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: COLORS.textDark,
  },
  // Error styles
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    backgroundColor: COLORS.background,
  },
  errorText: {
    fontSize: 16,
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 20,
  },
  errorButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorButtonText: {
    color: COLORS.textLight,
    fontSize: 16,
    fontWeight: "bold",
  },
});

export default ChatBot;
